import { useEffect, useState } from 'react'
import { Button, Card, Form, Input, message, Typography } from 'antd'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import apiClient from '../../api/client'
import LanguageSwitcher from '../../components/LanguageSwitcher'
import { captureReferralFromURL } from '../../utils/referralCapture'

interface LoginValues {
  email: string
  password: string
}

interface LoginResponse {
  totp_required?: boolean
  challenge_token?: string
}

export default function PortalLoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [challenge, setChallenge] = useState<string | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [signupEnabled, setSignupEnabled] = useState(false)

  const afterLoginTarget = searchParams.get('redirect') || '/portal'

  useEffect(() => {
    captureReferralFromURL()
    apiClient
      .get<{ data: { enabled: boolean } }>('/api/v1/signup/status')
      .then((res) => setSignupEnabled(res.data.data.enabled))
      .catch(() => setSignupEnabled(false))
  }, [])

  const onLogin = async (vals: LoginValues) => {
    setSubmitting(true)
    try {
      const res = await apiClient.post<{ data: LoginResponse }>(
        '/api/v1/portal/auth/login', vals,
      )
      if (res.data.data.totp_required && res.data.data.challenge_token) {
        setChallenge(res.data.data.challenge_token)
        return
      }
      navigate(afterLoginTarget)
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      void message.error(err.response?.data?.message || t('portalLogin.failed', 'Anmeldung fehlgeschlagen'))
    } finally {
      setSubmitting(false)
    }
  }

  const onVerify = async () => {
    if (!challenge) return
    setSubmitting(true)
    try {
      const payload: Record<string, string> = { challenge_token: challenge }
      if (useRecovery) {
        payload.recovery_code = otpCode.trim()
      } else {
        payload.code = otpCode.trim()
      }
      await apiClient.post('/api/v1/portal/auth/verify-totp', payload)
      navigate(afterLoginTarget)
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      void message.error(err.response?.data?.message ||
        t('portalLogin.failed', 'Anmeldung fehlgeschlagen'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="portal-login-wrapper">
      <Card className="portal-login-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <LanguageSwitcher />
        </div>
        <Typography.Title level={3} className="portal-login-title">
          {t('portalLogin.title', 'Kundenportal')}
        </Typography.Title>
        {challenge ? (
          <>
            <Typography.Paragraph type="secondary">
              {t('portalLogin.totpSubtitle',
                'Bitte gib den 6-stelligen Code aus deiner Authenticator-App ein, oder einen Recovery-Code.')}
            </Typography.Paragraph>
            <Form layout="vertical" onFinish={onVerify}>
              <Form.Item
                label={useRecovery
                  ? t('portalLogin.recoveryCode', 'Recovery-Code')
                  : t('portalLogin.totpCode', '6-stelliger Code')}>
                <Input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  autoFocus
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  maxLength={useRecovery ? 10 : 6}
                  autoComplete="one-time-code"
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" block
                  loading={submitting}>
                  {t('portalLogin.verify', 'Bestätigen')}
                </Button>
              </Form.Item>
            </Form>
            <Typography.Paragraph className="text-center mt-8 text-sm">
              <a onClick={(e) => {
                e.preventDefault()
                setUseRecovery(v => !v)
                setOtpCode('')
              }}>
                {useRecovery
                  ? t('portalLogin.useTotp', 'Doch TOTP-Code nutzen')
                  : t('portalLogin.useRecovery', 'Recovery-Code stattdessen')}
              </a>
            </Typography.Paragraph>
          </>
        ) : (
          <>
            <Typography.Paragraph type="secondary">
              {t('portalLogin.subtitle', 'Bitte melde dich mit deinen Zugangsdaten an.')}
            </Typography.Paragraph>
            <Form layout="vertical" onFinish={onLogin}>
              <Form.Item
                label={t('portalLogin.email', 'E-Mail')}
                name="email"
                rules={[{ required: true, type: 'email' }]}
              >
                <Input autoComplete="email" autoFocus />
              </Form.Item>
              <Form.Item
                label={t('portalLogin.password', 'Passwort')}
                name="password"
                rules={[{ required: true }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" block
                  loading={submitting}>
                  {t('portalLogin.submit', 'Anmelden')}
                </Button>
              </Form.Item>
            </Form>
            <Typography.Paragraph className="text-center mt-8 text-sm">
              <Link to="/portal/reset-request">
                {t('portalLogin.forgot', 'Passwort vergessen?')}
              </Link>
            </Typography.Paragraph>
            {signupEnabled && (
              <Typography.Paragraph className="text-center text-sm">
                <Link to={`/portal/signup${searchParams.get('redirect') ? `?redirect=${encodeURIComponent(searchParams.get('redirect')!)}` : ''}`}>
                  {t('portalLogin.signupLink', 'Neuen Account erstellen')}
                </Link>
              </Typography.Paragraph>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
